import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, ToggleLeftIcon, ToggleRightIcon, ArrowUpIcon, ArrowDownIcon, FilterIcon, XIcon } from 'lucide-react';
import { RoundingRule } from './RoundingConfiguration';
import { StatusBadge } from '../../StatusBadge';
type SortField = 'name' | 'roundingType' | 'interval' | 'active';
type SortDirection = 'asc' | 'desc';
interface RoundingListProps {
  rules: RoundingRule[];
  onAdd: () => void;
  onEdit: (rule: RoundingRule) => void;
  onToggleActive: (id: string) => void;
  onDelete: (id: string) => void;
  locale: string;
}
export function RoundingList({
  rules,
  onAdd,
  onEdit,
  onToggleActive,
  onDelete,
  locale
}: RoundingListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    roundingType: '',
    active: ''
  });
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  const resetFilters = () => {
    setFilters({
      roundingType: '',
      active: ''
    });
    setSearchTerm('');
  };
  // Apply filters and sorting
  const filteredRules = rules.filter(rule => {
    // Search term filter
    const searchMatch = rule.name.toLowerCase().includes(searchTerm.toLowerCase()) || rule.description.toLowerCase().includes(searchTerm.toLowerCase());
    // Rounding type filter
    const typeMatch = !filters.roundingType || rule.roundingType === filters.roundingType;
    // Active status filter
    const activeMatch = filters.active === '' || filters.active === 'active' && rule.active || filters.active === 'inactive' && !rule.active;
    return searchMatch && typeMatch && activeMatch;
  });
  // Apply sorting
  const sortedRules = [...filteredRules].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'roundingType':
        comparison = a.roundingType.localeCompare(b.roundingType);
        break;
      case 'interval':
        comparison = a.interval - b.interval;
        break;
      case 'active':
        comparison = Number(a.active) - Number(b.active);
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  const roundingTypeOptions = [{
    value: 'Nearest',
    label: 'Nearest'
  }, {
    value: 'Up',
    label: 'Up'
  }, {
    value: 'Down',
    label: 'Down'
  }, {
    value: 'Swedish',
    label: 'Swedish'
  }, {
    value: 'Truncate',
    label: 'Truncate'
  }];
  return <div className="space-y-6" data-id="element-8663">
      <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-8664">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-8665">
          <div className="flex-1 max-w-md" data-id="element-8666">
            <div className="relative rounded-md shadow-sm" data-id="element-8667">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8668">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-8669" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search rounding rules..." data-id="element-8670" />
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-8671">
            <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8672">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-8673" />
              Filters
              {Object.values(filters).some(f => f !== '') && <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-8674">
                  {Object.values(filters).filter(f => f !== '').length}
                </span>}
            </button>
            <button onClick={onAdd} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8675">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-8676" />
              Add Rounding Rule
            </button>
          </div>
        </div>
        {/* Filter chips */}
        {Object.values(filters).some(f => f !== '') && <div className="mt-4 flex flex-wrap gap-2" data-id="element-8677">
            {filters.roundingType && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-8678">
                Type: {filters.roundingType}
                <button type="button" onClick={() => setFilters({
            ...filters,
            roundingType: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none" data-id="element-8679">
                  <XIcon className="h-3 w-3" data-id="element-8680" />
                </button>
              </span>}
            {filters.active && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-8681">
                Status: {filters.active === 'active' ? 'Active' : 'Inactive'}
                <button type="button" onClick={() => setFilters({
            ...filters,
            active: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none" data-id="element-8682">
                  <XIcon className="h-3 w-3" data-id="element-8683" />
                </button>
              </span>}
            <button type="button" onClick={resetFilters} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-8684">
              Clear All
            </button>
          </div>}
        {/* Expanded filters */}
        {showFilters && <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-4" data-id="element-8685">
            <div data-id="element-8686">
              <label htmlFor="roundingType" className="block text-sm font-medium text-gray-700" data-id="element-8687">
                Rounding Type
              </label>
              <select id="roundingType" name="roundingType" value={filters.roundingType} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-8688">
                <option value="" data-id="element-8689">All Types</option>
                {roundingTypeOptions.map(option => <option key={option.value} value={option.value} data-id="element-8690">
                    {option.label}
                  </option>)}
              </select>
            </div>
            <div data-id="element-8691">
              <label htmlFor="active" className="block text-sm font-medium text-gray-700" data-id="element-8692">
                Status
              </label>
              <select id="active" name="active" value={filters.active} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-8693">
                <option value="" data-id="element-8694">All Statuses</option>
                <option value="active" data-id="element-8695">Active</option>
                <option value="inactive" data-id="element-8696">Inactive</option>
              </select>
            </div>
          </div>}
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-8697">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-8698">
          <thead className="bg-gray-50" data-id="element-8699">
            <tr data-id="element-8700">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('name')} data-id="element-8701">
                <div className="flex items-center space-x-1" data-id="element-8702">
                  <span data-id="element-8703">Name</span>
                  {sortField === 'name' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-8704" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-8705" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8706">
                Description
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('roundingType')} data-id="element-8707">
                <div className="flex items-center space-x-1" data-id="element-8708">
                  <span data-id="element-8709">Rounding Type</span>
                  {sortField === 'roundingType' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-8710" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-8711" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('interval')} data-id="element-8712">
                <div className="flex items-center space-x-1" data-id="element-8713">
                  <span data-id="element-8714">Interval</span>
                  {sortField === 'interval' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-8715" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-8716" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('active')} data-id="element-8717">
                <div className="flex items-center space-x-1" data-id="element-8718">
                  <span data-id="element-8719">Status</span>
                  {sortField === 'active' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-8720" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-8721" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8722">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-8723">
            {sortedRules.length === 0 ? <tr data-id="element-8724">
                <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-8725">
                  No rounding rules found. Click "Add Rounding Rule" to create
                  one.
                </td>
              </tr> : sortedRules.map(rule => <tr key={rule.id} className="hover:bg-gray-50" data-id="element-8726">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-8727">
                    {rule.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500" data-id="element-8728">
                    {rule.description || <span className="text-gray-400 italic" data-id="element-8729">
                        No description
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-8730">
                    {rule.roundingType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-8731">
                    {rule.interval.toLocaleString(locale, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-8732">
                    <StatusBadge status={rule.active ? 'success' : 'error'} text={rule.active ? 'Active' : 'Inactive'} data-id="element-8733" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-8734">
                    <div className="flex justify-end space-x-2" data-id="element-8735">
                      <button onClick={() => onEdit(rule)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-8736">
                        <EditIcon className="h-4 w-4" data-id="element-8737" />
                      </button>
                      <button onClick={() => onToggleActive(rule.id)} className={`${rule.active ? 'text-green-600 hover:text-green-900' : 'text-gray-400 hover:text-gray-600'}`} title={rule.active ? 'Deactivate' : 'Activate'} data-id="element-8738">
                        {rule.active ? <ToggleRightIcon className="h-4 w-4" data-id="element-8739" /> : <ToggleLeftIcon className="h-4 w-4" data-id="element-8740" />}
                      </button>
                      <button onClick={() => onDelete(rule.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-8741">
                        <TrashIcon className="h-4 w-4" data-id="element-8742" />
                      </button>
                    </div>
                  </td>
                </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}